import React, { forwardRef, useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckBox,
  Dialog,
  DialogBackdrop,
  DialogContent,
  DialogFooter,
  PrimaryButton,
  TertiaryButton,
  useDialogState,
} from '@tricor-digital-team/vistra-design-system';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { AxiosClient } from '../../../../Utils/AxiosClient';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { VistraLoader } from '@tricor-digital-team/vistra-design-system';
import PaymentFailure from './PaymentFailure';
import CancelStep from '../CancelStep';
import { resetFormData } from '../../../../Redux/formSlice';
import { resetIndividual } from '../../../../Redux/individualSlice';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentPage = ({ setCurrentStep }) => {
  const storedFormData = useSelector((state) => state.form.formData);
  const individualDetails = useSelector((state) => state.individual.individualDetails);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dialog = useDialogState({ visible: false });
  const failureDialog = useDialogState({ visible: false });
  const cancelDialog = useDialogState({ visible: false });

  const [clientSecret, setClientSecret] = useState('');
  const [refCode, setRefCode] = useState('');
  const [currency, setCurrency] = useState({ unitAmount: null, taxAmount: null, currency: null });
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [receiveNotification, setReceiveNotification] = useState(false);

  const fetchForm = async () => {
    setLoading(true);
    try {
      const response = await AxiosClient.get('getPaymentDetails');
      setCurrency(response.data);
    } catch (error) {
      console.error('Error fetching form:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForm();
  }, []);

  const handleConfirm = async () => {
    setLoadingConfirm(true);
    try {
      const submissionRes = await AxiosClient.post('submitForm', {
        requestorInformation: { ...storedFormData, receiveNotification },
        individuals: individualDetails,
      });

      const submissionId = submissionRes.data.refCode;
      setRefCode(submissionId);
      setConfirmed(true);

      const paymentRes = await AxiosClient.post('initiatePayment', {
        submissionId,
      });

      setClientSecret(paymentRes.data.clientSecret);
      dialog.show();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleTryAgain = () => {
    failureDialog.hide();
  };

  const individualTotal = individualDetails.length * (currency?.unitAmount / 100);
  const individualTax = individualTotal * (currency?.taxAmount / 100);
  const total = individualTotal + individualTax;

  return (
    <div>
      {!loading ? (
        <>
          <div className="text-[#68696C] text-sm">Check the details before proceeding to payment.</div>
          <div className="text-black text-2xl mt-6 font-normal">Total cost</div>
        </>
      ) : ''}
      <div className={`overflow-x-auto mt-6 ${!loading && 'border-t-2 border-gray-200'}`}>
        <table class="min-w-full border-collapse">
          {loading ? (
            <tr>
              <td colSpan="3">
                <div className="h-[200px] flex items-center justify-center">
                  <VistraLoader />
                </div>
              </td>
            </tr>
          ) : (<>
            <thead class="border-b border-gray-200">
              <tr>
                <th class="py-2 text-xs font-medium text-gray-900 text-left">
                  <div class="p-2">Items</div>
                </th>
                <th class="py-2 text-xs font-medium text-gray-900 text-left">
                  <div class="p-2">Quantity</div>
                </th>
                <th class="py-2 text-xs font-medium text-gray-900 text-right">
                  <div class="p-2">Price ({currency?.currency?.toUpperCase()})</div>
                </th>
              </tr>
            </thead>
            <tbody>


              <tr className="border-b border-gray-200">
                <td class="px-2 py-3 text-gray-900 text-sm">Individual Identity Verification</td>
                <td class="px-2 py-3 text-gray-900 text-sm">{'x' + individualDetails.length}</td>
                <td class="px-2 py-3 text-gray-900 text-sm text-right">{(individualTotal)?.toLocaleString('en-UK')}</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td class="px-2 py-3 text-gray-900 text-sm">VAT (per individual)</td>
                <td class="px-2 py-3 text-gray-900 text-sm">{'x' + individualDetails.length}</td>
                <td class="px-2 py-3 text-gray-900 text-sm text-right">{(individualTax)?.toLocaleString('en-UK')}</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td class="p-2 py-2 text-gray-900 text-base font-medium">Total</td>
                <td class="px-2 py-3 text-gray-900 text-sm"></td>
                <td class="px-2 py-3 text-gray-900 text-base text-right font-medium">{(total)?.toLocaleString('en-UK')}</td>
              </tr>
            </tbody>
          </>
          )}
        </table>
      </div>

      {/* <div className="text-black text-base mt-12">
        <CheckBox
          handleOnChange={(e) => setReceiveNotification(e.target.checked)}
          size="large"
          label={
            <div className="font-base">I would like to receive all email notifications regarding status updates</div>
          }
        />
      </div> */}

      <div className="flex justify-between items-center mt-8">
        <TertiaryButton label="Cancel" onClick={() => cancelDialog.show()} size="large" disabled={loadingConfirm} />
        <div className="flex justify-between gap-6">
          <TertiaryButton
            icon={ArrowLeftIcon}
            iconPosition="left"
            label="Back"
            onClick={() => setCurrentStep(2)}
            disabled={loadingConfirm}
            size="large"
          />
          <PrimaryButton
            icon={ArrowRightIcon}
            iconPosition="right"
            label={loadingConfirm ? 'Processing...' : 'Confirm and pay'}
            onClick={handleConfirm}
            size="large"
            disabled={loadingConfirm || loading}
          />
        </div>
      </div>

      <CancelStep
        dialog={cancelDialog}
        handleExit={() => {
          dispatch(resetFormData());
          dispatch(resetIndividual());
          cancelDialog.hide();
          navigate('/');
        }}
      />

      {confirmed && clientSecret && (
        <DialogBackdrop id="dialog-backdrop" {...dialog}>
          <Dialog
            id="dialog-container"
            hideOnEsc={false}
            hideOnClickOutside={false}
            {...dialog}
            aria-label="Payment"
            size="small"
          >
            <DialogContent>
              <div className="">
                <div className="text-2xl ">Payment method</div>
                <TertiaryButton
                  icon={ArrowLeftIcon}
                  iconPosition="left"
                  label="Back to payment detail"
                  onClick={(e) => {
                    e.preventDefault();
                    dialog.hide();
                  }}
                  size="medium"
                  extraClass="mb-6 text-blue-700 font-medium "
                />
                <div class='border-b-2 border-gray-100'></div>
                <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            </DialogContent>
            {/* <DialogFooter>
              <div className="flex justify-between items-center w-full py-3">
                <TertiaryButton
                  label="Cancel"
                  onClick={(e) => {
                    e.preventDefault();
                    dialog.hide();
                  }}
                  size="large"
                />
              </div>
            </DialogFooter> */}
          </Dialog>
        </DialogBackdrop>
      )}

      <PaymentFailure dialog={failureDialog} handleTryAgain={handleTryAgain} />
    </div>
  );
};

export default PaymentPage;
